import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import ProtectedRoute from "@/components/layout/ProtectedRoute";
import AdminProtectedRoute from "@/components/layout/AdminProtectedRoute";
import AppLayout from "@/components/layout/AppLayout";
import MarketingLayout from "@/components/layout/MarketingLayout";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminAccountsPage from "@/pages/admin/AdminAccountsPage";
import AdminCommercialPage from "@/pages/admin/AdminCommercialPage";
import AdminSupportPage from "@/pages/admin/AdminSupportPage";
import AdminTechnicalPage from "@/pages/admin/AdminTechnicalPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";

import LandingPage from "@/pages/marketing/LandingPage";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";

import DashboardPage from "@/pages/app/DashboardPage";
import CompaniesShell from "@/pages/app/companies/CompaniesShell";
import GroupOverviewTab from "@/pages/app/companies/GroupOverviewTab";
import EntitiesTab from "@/pages/app/companies/EntitiesTab";
import OrgChartTab from "@/pages/app/companies/OrgChartTab";
import IntragroupFlowsTab from "@/pages/app/companies/IntragroupFlowsTab";
import CompanyDetailPage from "@/pages/app/companies/CompanyDetailPage";
import PatrimoinePage from "@/pages/app/PatrimoinePage";
import BuildingsListPage from "@/pages/app/buildings/BuildingsListPage";
import BuildingDetailPage from "@/pages/app/buildings/BuildingDetailPage";
import { CreditsListPage, CreditDetailPage } from "@/pages/app/CreditsPage";
import PerformancePage from "@/pages/app/PerformancePage";
import DocumentsPage from "@/pages/app/DocumentsPage";
import DocumentVaultPage from "@/pages/app/DocumentVaultPage";
import AnalysisPage from "@/pages/app/AnalysisPage";
import CommandCenterPage from "@/pages/app/CommandCenterPage";
import AccountingPage from "@/pages/app/AccountingPage";
import DeadlinesPage from "@/pages/app/DeadlinesPage";
import MdbPage from "@/pages/app/MdbPage";
import LocationsPage from "@/pages/app/LocationsPage";
import InventoriesPage from "@/pages/app/InventoriesPage";
import InventoryEditor from "@/pages/app/InventoryEditor";
import TenantPortalApp from "@/pages/tenant/TenantPortal";
import SettingsPage from "@/pages/app/SettingsPage";
import PlatformAdminPage from "@/pages/app/PlatformAdminPage";
import HelpPage from "@/pages/app/HelpPage";
import LeaseWizardPage from "@/pages/app/LeaseWizardPage";
import CompliancePage from "@/pages/app/CompliancePage";
import MyAssistantPage from "@/pages/app/MyAssistantPage";
import SubscriptionPage from "@/pages/app/SubscriptionPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentPages";
import {
  FinancesPage, EcheancesPage, ProjetsPage, AssistantPage, RapportsPage,
} from "@/pages/app/PlaceholderPages";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Marketing */}
            <Route path="/" element={<LandingPage />} />
            <Route element={<MarketingLayout />}>
              {/* Legacy marketing layout kept for other future public pages */}
            </Route>

            {/* Auth */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* Stripe return URLs (public — no auth required) */}
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />

            {/* Tenant portal (own auth) */}
            <Route path="/tenant/*" element={<TenantPortalApp />} />
            {/* /fonbex/* showcase retiré de la production — le composant
                MigrationDiff / FonbexShowcase reste dans le dépôt à des fins
                documentaires internes mais n'est plus routé publiquement. */}
            <Route path="/locataire" element={<Navigate to="/tenant/login" replace />} />
            <Route path="/locataire/*" element={<Navigate to="/tenant/login" replace />} />

            {/* App (protected) */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <ErrorBoundary>
                    <AppLayout />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="assistant" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="companies" element={<CompaniesShell />}>
                <Route index element={<GroupOverviewTab />} />
                <Route path="entities" element={<EntitiesTab />} />
                <Route path="orgchart" element={<OrgChartTab />} />
                <Route path="flows" element={<IntragroupFlowsTab />} />
                <Route path=":id" element={<CompanyDetailPage />} />
              </Route>
              <Route path="patrimoine" element={<PatrimoinePage />} />
              <Route path="buildings" element={<BuildingsListPage />} />
              <Route path="buildings/:id" element={<BuildingDetailPage />} />
              <Route path="credits" element={<CreditsListPage />} />
              <Route path="credits/:id" element={<CreditDetailPage />} />
              <Route path="performance" element={<PerformancePage />} />
              <Route path="locations" element={<LocationsPage />} />
              <Route path="inventories" element={<InventoriesPage />} />
              <Route path="inventories/:id" element={<InventoryEditor />} />
              <Route path="finances" element={<FinancesPage />} />
              <Route path="documents" element={<DocumentVaultPage />} />
              <Route path="documents/legacy" element={<DocumentsPage />} />
              <Route path="analysis" element={<AnalysisPage />} />
              <Route path="command-center" element={<CommandCenterPage />} />
              <Route path="accounting" element={<AccountingPage />} />
              <Route path="deadlines" element={<DeadlinesPage />} />
              <Route path="mdb" element={<MdbPage />} />
              <Route path="echeances" element={<EcheancesPage />} />
              <Route path="projets" element={<ProjetsPage />} />
              <Route path="assistant" element={<MyAssistantPage />} />
              <Route path="rapports" element={<RapportsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="platform-admin" element={<Navigate to="/admin" replace />} />
              <Route path="help" element={<HelpPage />} />
              <Route path="new-lease" element={<LeaseWizardPage />} />
              <Route path="compliance" element={<CompliancePage />} />
              <Route path="subscription" element={<SubscriptionPage />} />
            </Route>

            {/* Back-office plateforme (Super Admin & Fondateur uniquement) */}
            <Route
              path="/admin"
              element={
                <AdminProtectedRoute>
                  <AdminLayout />
                </AdminProtectedRoute>
              }
            >
              <Route index element={<AdminDashboardPage />} />
              <Route path="accounts" element={<AdminAccountsPage />} />
              <Route path="commercial" element={<AdminCommercialPage />} />
              <Route path="support" element={<AdminSupportPage />} />
              <Route path="technical" element={<AdminTechnicalPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
